import Konva from 'konva';
import type { Box } from 'konva/lib/shapes/Transformer';
import { EMagicPenShape, ICanvasObject } from '@src/types';

/**
 * Minimum dimensions for different object types
 * These ensure objects remain visible and interactable
 */
export const MIN_DIMENSIONS = {
  [EMagicPenShape.STICKY_NOTE]: { width: 80, height: 50 },
  [EMagicPenShape.TEXT_BOX]: { width: 100, height: 40 },
  [EMagicPenShape.STICKER]: { width: 30, height: 30 },
  [EMagicPenShape.RECTANGLE]: { width: 20, height: 20 },
  [EMagicPenShape.CIRCLE]: { width: 20, height: 20 },
  [EMagicPenShape.LINE]: { width: 10, height: 10 },
  [EMagicPenShape.ARROW]: { width: 20, height: 20 },
  DEFAULT: { width: 5, height: 5 },
} as const;

/**
 * Get minimum dimensions for a specific object type
 */
export const getMinDimensions = (objectType?: EMagicPenShape): { width: number; height: number } => {
  if (!objectType) return MIN_DIMENSIONS.DEFAULT;
  return MIN_DIMENSIONS[objectType] || MIN_DIMENSIONS.DEFAULT;
};

/**
 * Constrain position to keep object within canvas bounds during drag
 * @param pos - Attempted position {x, y}
 * @param objectWidth - Width of the object
 * @param objectHeight - Height of the object
 * @param stage - Konva Stage reference
 * @returns Constrained position that keeps object fully visible
 */
export const constrainDragPosition = (
  pos: { x: number; y: number },
  objectWidth: number,
  objectHeight: number,
  stage: Konva.Stage | null
): { x: number; y: number } => {
  if (!stage) return pos;

  const stageWidth = stage.width();
  const stageHeight = stage.height();

  // Constrain x position (left/right edges)
  const newX = Math.max(0, Math.min(pos.x, stageWidth - objectWidth));

  // Constrain y position (top/bottom edges)
  const newY = Math.max(0, Math.min(pos.y, stageHeight - objectHeight));

  return { x: newX, y: newY };
};

/**
 * Create a dragBoundFunc for Konva objects with dynamic dimension lookup
 * This function can be directly assigned to a Konva node's dragBoundFunc property
 * Gets dimensions dynamically to handle resized objects correctly
 */
export const createDragBoundFuncWithGetter = (
  getDimensions: () => { width: number; height: number },
  stageRef: React.RefObject<Konva.Stage>
) => {
  return function (this: any, pos: { x: number; y: number }) {
    const stage = stageRef.current;
    if (!stage) return pos;

    // Get current dimensions dynamically
    const { width: currentWidth, height: currentHeight } = getDimensions();

    return constrainDragPosition(pos, currentWidth, currentHeight, stage);
  };
};

/**
 * Create a dragBoundFunc for Konva objects (legacy version for backward compatibility)
 * This function can be directly assigned to a Konva node's dragBoundFunc property
 */
export const createDragBoundFunc = (
  objectWidth: number,
  objectHeight: number,
  stageRef: React.RefObject<Konva.Stage>
) => {
  return function (this: any, pos: { x: number; y: number }) {
    return constrainDragPosition(pos, objectWidth, objectHeight, stageRef.current);
  };
};

/**
 * Constrain initial position when creating a new object
 * Useful for placing objects near canvas edges
 * @param clickPos - Where user clicked {x, y}
 * @param objectWidth - Width of the new object
 * @param objectHeight - Height of the new object
 * @param offset - Optional offset to center object on click point
 * @param stage - Konva Stage reference
 */
export const constrainInitialPosition = (
  clickPos: { x: number; y: number },
  objectWidth: number,
  objectHeight: number,
  stage: Konva.Stage | null,
  offset: { x: number; y: number } = { x: 0, y: 0 }
): { x: number; y: number } => {
  if (!stage) {
    return {
      x: clickPos.x + offset.x,
      y: clickPos.y + offset.y,
    };
  }

  const stageWidth = stage.width();
  const stageHeight = stage.height();

  // Calculate desired position with offset (e.g., centering)
  const desiredX = clickPos.x + offset.x;
  const desiredY = clickPos.y + offset.y;

  // Constrain to canvas bounds
  const constrainedX = Math.max(0, Math.min(desiredX, stageWidth - objectWidth));
  const constrainedY = Math.max(0, Math.min(desiredY, stageHeight - objectHeight));

  return { x: constrainedX, y: constrainedY };
};

/**
 * Constrain dimensions during resize to stay within canvas bounds
 * @param currentPos - Current object position {x, y}
 * @param newWidth - Proposed new width
 * @param newHeight - Proposed new height
 * @param objectType - Type of object being resized
 * @param stage - Konva Stage reference
 * @returns Constrained dimensions {width, height}
 */
export const constrainResizeDimensions = (
  currentPos: { x: number; y: number },
  newWidth: number,
  newHeight: number,
  objectType: EMagicPenShape | undefined,
  stage: Konva.Stage | null
): { width: number; height: number } => {
  // Get minimum dimensions for this object type
  const minDimensions = getMinDimensions(objectType);

  // Apply minimum constraints first
  let constrainedWidth = Math.max(minDimensions.width, newWidth);
  let constrainedHeight = Math.max(minDimensions.height, newHeight);

  // Apply canvas boundary constraints if stage exists
  if (stage) {
    const stageWidth = stage.width();
    const stageHeight = stage.height();

    // Calculate maximum allowed dimensions based on position
    const maxWidth = stageWidth - currentPos.x;
    const maxHeight = stageHeight - currentPos.y;

    // Constrain to canvas bounds (but respect minimum)
    constrainedWidth = Math.max(minDimensions.width, Math.min(constrainedWidth, maxWidth));
    constrainedHeight = Math.max(minDimensions.height, Math.min(constrainedHeight, maxHeight));
  }

  return {
    width: constrainedWidth,
    height: constrainedHeight,
  };
};

/**
 * Create a boundBoxFunc for Konva Transformer
 * Prevents resizing below minimum dimensions and outside canvas bounds (all 4 edges)
 */
export const createBoundBoxFunc = (objectType?: EMagicPenShape, stage?: Konva.Stage | null) => {
  const minDimensions = getMinDimensions(objectType);

  return (oldBox: Box, newBox: Box): Box => {
    // Prevent resizing below minimum dimensions
    if (newBox.width < minDimensions.width || newBox.height < minDimensions.height) {
      return oldBox;
    }

    // Prevent resizing outside canvas bounds (all 4 edges)
    if (stage) {
      const stageWidth = stage.width();
      const stageHeight = stage.height();

      // Check if any edge would go outside canvas bounds
      const wouldExceedLeft = newBox.x < 0;
      const wouldExceedTop = newBox.y < 0;
      const wouldExceedRight = newBox.x + newBox.width > stageWidth;
      const wouldExceedBottom = newBox.y + newBox.height > stageHeight;

      // If any edge would exceed bounds, reject the entire resize to maintain proportions
      if (wouldExceedLeft || wouldExceedTop || wouldExceedRight || wouldExceedBottom) {
        return oldBox;
      }
    }

    return newBox;
  };
};

/**
 * Utility to check if an object is within canvas bounds
 * Useful for validation and testing
 */
export const isWithinCanvasBounds = (
  pos: { x: number; y: number },
  width: number,
  height: number,
  stage: Konva.Stage | null
): boolean => {
  if (!stage) return true;

  const stageWidth = stage.width();
  const stageHeight = stage.height();

  return pos.x >= 0 && pos.y >= 0 && pos.x + width <= stageWidth && pos.y + height <= stageHeight;
};

/**
 * Constrain drawing position to keep shapes within canvas bounds during creation
 * @param startPos - Starting position {x, y}
 * @param currentPos - Current position {x, y}
 * @param stage - Konva Stage reference
 * @returns Constrained current position that keeps shape fully visible
 */
export const constrainDrawingPosition = (
  startPos: { x: number; y: number },
  currentPos: { x: number; y: number },
  stage: Konva.Stage | null
): { x: number; y: number } => {
  if (!stage) return currentPos;

  const stageWidth = stage.width();
  const stageHeight = stage.height();

  // Constrain x position (left/right edges)
  const newX = Math.max(Math.min(startPos.x, stageWidth), Math.min(currentPos.x, stageWidth));

  // Constrain y position (top/bottom edges)
  const newY = Math.max(Math.min(startPos.y, stageHeight), Math.min(currentPos.y, stageHeight));

  return { x: newX, y: newY };
};

/**
 * Constrain circle drawing to keep it within canvas bounds
 * @param center - Circle center {x, y}
 * @param radius - Circle radius
 * @param stage - Konva Stage reference
 * @returns Constrained radius that keeps circle fully visible
 */
export const constrainCircleDrawing = (
  center: { x: number; y: number },
  radius: number,
  stage: Konva.Stage | null
): number => {
  if (!stage) return radius;

  const stageWidth = stage.width();
  const stageHeight = stage.height();

  // Calculate maximum radius that keeps circle within bounds
  const maxRadiusX = Math.min(center.x, stageWidth - center.x);
  const maxRadiusY = Math.min(center.y, stageHeight - center.y);
  const maxRadius = Math.min(maxRadiusX, maxRadiusY);

  return Math.max(0, Math.min(radius, maxRadius));
};

/**
 * Constrain rectangle drawing to keep it within canvas bounds
 * @param startPos - Starting position {x, y} (top-left corner)
 * @param currentPos - Current position {x, y}
 * @param stage - Konva Stage reference
 * @returns Constrained width and height that keeps rectangle fully visible
 */
export const constrainRectangleDrawing = (
  startPos: { x: number; y: number },
  currentPos: { x: number; y: number },
  stage: Konva.Stage | null
): { width: number; height: number } => {
  if (!stage) {
    return {
      width: currentPos.x - startPos.x,
      height: currentPos.y - startPos.y,
    };
  }

  const stageWidth = stage.width();
  const stageHeight = stage.height();

  // Calculate the actual bounds of the rectangle (before constraining)
  const rectLeft = Math.min(startPos.x, currentPos.x);
  const rectRight = Math.max(startPos.x, currentPos.x);
  const rectTop = Math.min(startPos.y, currentPos.y);
  const rectBottom = Math.max(startPos.y, currentPos.y);

  // Constrain the rectangle bounds to canvas bounds
  const constrainedLeft = Math.max(0, rectLeft);
  const constrainedRight = Math.min(stageWidth, rectRight);
  const constrainedTop = Math.max(0, rectTop);
  const constrainedBottom = Math.min(stageHeight, rectBottom);

  // Calculate constrained width and height from the start position
  // This preserves the direction of drawing (positive or negative width/height)
  let constrainedWidth: number;
  let constrainedHeight: number;

  if (currentPos.x < startPos.x) {
    // Drawing from right to left - width should be negative
    constrainedWidth = constrainedLeft - startPos.x;
  } else {
    // Drawing from left to right - width should be positive
    constrainedWidth = constrainedRight - startPos.x;
  }

  if (currentPos.y < startPos.y) {
    // Drawing from bottom to top - height should be negative
    constrainedHeight = constrainedTop - startPos.y;
  } else {
    // Drawing from top to bottom - height should be positive
    constrainedHeight = constrainedBottom - startPos.y;
  }

  return {
    width: constrainedWidth,
    height: constrainedHeight,
  };
};

/**
 * Calculate the intersection point where a line from lastPoint to currentPoint
 * crosses the canvas edge. Used for pen/ephemeral pen to extend lines to canvas edge
 * when mouse leaves the canvas.
 * @param lastPoint - Last valid point within canvas bounds {x, y}
 * @param currentPoint - Current mouse position (may be outside canvas) {x, y}
 * @param stageWidth - Canvas width
 * @param stageHeight - Canvas height
 * @returns Intersection point on canvas edge {x, y}
 */
export const calculateEdgeIntersection = (
  lastPoint: { x: number; y: number },
  currentPoint: { x: number; y: number },
  stageWidth: number,
  stageHeight: number
): { x: number; y: number } => {
  // Calculate direction vector
  const dx = currentPoint.x - lastPoint.x;
  const dy = currentPoint.y - lastPoint.y;

  // If no movement, return last point
  if (dx === 0 && dy === 0) {
    return lastPoint;
  }

  // Calculate intersection with each edge
  // We'll find the closest intersection point

  // Calculate t values for each edge (0 to 1 represents position along the line)
  let minT = Infinity;
  let intersectionX = currentPoint.x;
  let intersectionY = currentPoint.y;

  // Left edge (x = 0)
  if (dx !== 0) {
    const t = (0 - lastPoint.x) / dx;
    if (t >= 0 && t <= 1) {
      const y = lastPoint.y + t * dy;
      if (y >= 0 && y <= stageHeight) {
        if (t < minT) {
          minT = t;
          intersectionX = 0;
          intersectionY = y;
        }
      }
    }
  }

  // Right edge (x = stageWidth)
  if (dx !== 0) {
    const t = (stageWidth - lastPoint.x) / dx;
    if (t >= 0 && t <= 1) {
      const y = lastPoint.y + t * dy;
      if (y >= 0 && y <= stageHeight) {
        if (t < minT) {
          minT = t;
          intersectionX = stageWidth;
          intersectionY = y;
        }
      }
    }
  }

  // Top edge (y = 0)
  if (dy !== 0) {
    const t = (0 - lastPoint.y) / dy;
    if (t >= 0 && t <= 1) {
      const x = lastPoint.x + t * dx;
      if (x >= 0 && x <= stageWidth) {
        if (t < minT) {
          minT = t;
          intersectionX = x;
          intersectionY = 0;
        }
      }
    }
  }

  // Bottom edge (y = stageHeight)
  if (dy !== 0) {
    const t = (stageHeight - lastPoint.y) / dy;
    if (t >= 0 && t <= 1) {
      const x = lastPoint.x + t * dx;
      if (x >= 0 && x <= stageWidth) {
        if (t < minT) {
          minT = t;
          intersectionX = x;
          intersectionY = stageHeight;
        }
      }
    }
  }

  // Clamp to bounds in case we didn't find an intersection
  intersectionX = Math.max(0, Math.min(intersectionX, stageWidth));
  intersectionY = Math.max(0, Math.min(intersectionY, stageHeight));

  return { x: intersectionX, y: intersectionY };
};

//TODO: Move these functions to a dedicated file for canvas normalization
/**
 * Convert absolute coordinates to relative coordinates (0-1 scale)
 * @param x - Absolute x coordinate
 * @param y - Absolute y coordinate
 * @param width - Container width
 * @param height - Container height
 * @returns Relative coordinates {x, y} in 0-1 scale
 */

export const toRelativePosition = (x: number, y: number, width: number, height: number) => {
  return {
    x: width > 0 ? x / width : 0,
    y: height > 0 ? y / height : 0,
  };
};

/**
 * Convert relative coordinates (0-1 scale) to absolute coordinates
 * @param x - Relative x coordinate (0-1)
 * @param y - Relative y coordinate (0-1)
 * @param width - Container width
 * @param height - Container height
 * @returns Absolute coordinates {x, y}
 */
export const toAbsolutePosition = (x: number, y: number, width: number, height: number) => {
  return {
    x: x * width,
    y: y * height,
  };
};

/**
 * Convert absolute dimensions to relative dimensions (0-1 scale)
 * @param width - Absolute width
 * @param height - Absolute height
 * @param containerWidth - Container width
 * @param containerHeight - Container height
 * @returns Relative dimensions {width, height} in 0-1 scale
 */
export const toRelativeDimensions = (
  width: number,
  height: number,
  containerWidth: number,
  containerHeight: number
) => {
  return {
    width: containerWidth > 0 ? width / containerWidth : 0,
    height: containerHeight > 0 ? height / containerHeight : 0,
  };
};

/**
 * Convert relative dimensions (0-1 scale) to absolute dimensions
 * @param width - Relative width (0-1)
 * @param height - Relative height (0-1)
 * @param containerWidth - Container width
 * @param containerHeight - Container height
 * @returns Absolute dimensions {width, height}
 */
export const toAbsoluteDimensions = (
  width: number,
  height: number,
  containerWidth: number,
  containerHeight: number
) => {
  return {
    width: width * containerWidth,
    height: height * containerHeight,
  };
};

/**
 * Calculate responsive font size based on container dimensions
 * @param baseFontSize - The base font size to scale from
 * @param containerWidth - Current container width
 * @param containerHeight - Current container height
 * @returns Responsive font size that scales with container dimensions
 */
export const calculateResponsiveFontSize = (baseFontSize: number, containerWidth: number, containerHeight: number) => {
  // Use the smaller of width or height to determine scaling factor
  const minDimension = Math.min(containerWidth, containerHeight);
  // Scale font size based on container size (with a minimum size)
  const scaleFactor = Math.max(0.5, minDimension / 800); // 800px as reference size
  return Math.max(10, baseFontSize * scaleFactor); // Minimum 10px font size
};

/**
 * Add relative coordinates / dimensions / points to a single object
 */
export const toRelativeCanvasObject = (
  object: ICanvasObject,
  containerWidth: number,
  containerHeight: number
): ICanvasObject => {
  const withRelative: ICanvasObject = { ...object };

  // Position → relativeX / relativeY
  if (object.x !== undefined && object.y !== undefined) {
    const { x, y } = toRelativePosition(object.x, object.y, containerWidth, containerHeight);
    withRelative.relativeX = x;
    withRelative.relativeY = y;
  }

  // Size → relativeWidth / relativeHeight
  if (object.width !== undefined && object.height !== undefined) {
    const { width, height } = toRelativeDimensions(object.width, object.height, containerWidth, containerHeight);
    withRelative.relativeWidth = width;
    withRelative.relativeHeight = height;
  }

  // Lines / freehand strokes
  if (object.relativePoints && object.relativePoints.length > 0) {
    // Already relative → just ensure consistency
    withRelative.points = [...object.relativePoints];
    withRelative.relativePoints = [...object.relativePoints];
  } else if (object.points && object.points.length > 0) {
    // Absolute points → convert to relative
    const relativePoints = object.points.map((point, index) => {
      const size = index % 2 === 0 ? containerWidth : containerHeight;
      return size > 0 ? point / size : 0;
    });

    withRelative.points = relativePoints;
    withRelative.relativePoints = [...relativePoints];
  }

  return withRelative;
};

/**
 * Convert stored relative geometry into absolute coordinates/dimensions for the current stage size
 * Ensures absolute + relative props stay in sync so consumers can rely on either representation
 */
export const toAbsoluteCanvasObject = (
  object: ICanvasObject,
  containerWidth: number,
  containerHeight: number
): ICanvasObject => {
  const withAbsolute: ICanvasObject = { ...object };

  // When a client joins mid-session we often receive only relative geometry
  // Populate the absolute fields Konva expects so visuals line up with the local stage size
  if (containerWidth > 0) {
    if (object.relativeX !== undefined) {
      withAbsolute.x = object.relativeX * containerWidth;
    }
    if (object.relativeWidth !== undefined) {
      withAbsolute.width = object.relativeWidth * containerWidth;
    }
  }

  if (containerHeight > 0) {
    if (object.relativeY !== undefined) {
      withAbsolute.y = object.relativeY * containerHeight;
    }
    if (object.relativeHeight !== undefined) {
      withAbsolute.height = object.relativeHeight * containerHeight;
    }
  }

  // Ensure relative values exist if only absolute were provided (defensive)
  if (object.x !== undefined && containerWidth > 0 && object.relativeX === undefined) {
    withAbsolute.relativeX = object.x / containerWidth;
  }
  if (object.y !== undefined && containerHeight > 0 && object.relativeY === undefined) {
    withAbsolute.relativeY = object.y / containerHeight;
  }
  if (object.width !== undefined && containerWidth > 0 && object.relativeWidth === undefined) {
    withAbsolute.relativeWidth = object.width / containerWidth;
  }
  if (object.height !== undefined && containerHeight > 0 && object.relativeHeight === undefined) {
    withAbsolute.relativeHeight = object.height / containerHeight;
  }

  if (object.relativePoints && object.relativePoints.length > 0 && containerWidth > 0 && containerHeight > 0) {
    withAbsolute.points = object.relativePoints.map((point, index) => {
      const dimension = index % 2 === 0 ? containerWidth : containerHeight;
      return point * dimension;
    });
  } else if (object.points && object.points.length > 0 && containerWidth > 0 && containerHeight > 0) {
    // If we only had absolute points, backfill relatives so future resizes stay consistent
    withAbsolute.relativePoints = object.points.map((point, index) => {
      const dimension = index % 2 === 0 ? containerWidth : containerHeight;
      return dimension > 0 ? point / dimension : 0;
    });
  }

  return withAbsolute;
};
